import { useMemo, useState, useCallback, useEffect } from "react";
import { generateConversationId } from "../util/chatUtils";
import { getResponseService, getSummaryService, getConversationHistoryService } from "../../app/services/nairaService";
import { createTheme } from "@mui/material";
import { features } from "app/features";

export function useChatbotController({ publicationId, isHtmlReport, initialTab = 0 }) {
  const eqEnabled = features.chatbot.showEqCheckbox === true;
  const initialSelectedOptions = eqEnabled
    ? { all: true, economics: true, fx: true, fi: true, eq: true }
    : { all: true, economics: true, fx: true, fi: true };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [botResponseCount, setBotResponseCount] = useState(0);
  const [conversationId, setConversationId] = useState(() => generateConversationId());
  const [loading, setLoading] = useState(false);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [showIntroSection, setShowIntroSection] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [fullscreen, setFullscreen] = useState(false);
  const [deepSearch, setDeepSearch] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
  const [reportSummaryInput, setReportSummaryInput] = useState(publicationId != null ? String(publicationId) : "");
  const [reportSummaryError, setReportSummaryError] = useState("");
  const [reportSummaryData, setReportSummaryData] = useState("");
  const [reportSummaryLoading, setReportSummaryLoading] = useState(false);

  // Auto-fetch summary when opened from a card with a publicationId on the Summary tab
  useEffect(() => {
    if (initialTab === 1 && publicationId) {
      setReportSummaryLoading(true);
      getSummaryService(String(publicationId))
        .then((result) => setReportSummaryData(result.responseBody?.answer || ""))
        .catch(() => setReportSummaryData("Error fetching summary."))
        .finally(() => setReportSummaryLoading(false));
    }
  }, []);

  // Handle AI Summary button clicks when chatbot is already open
  useEffect(() => {
    const handler = (e) => {
      const pubId = e.detail?.publicationId;
      if (!pubId) return;
      const pubIdStr = String(pubId);
      setTab(1);
      setReportSummaryInput(pubIdStr);
      setReportSummaryError("");
      setReportSummaryData("");
      setReportSummaryLoading(true);
      getSummaryService(pubIdStr)
        .then((result) => setReportSummaryData(result.responseBody?.answer || ""))
        .catch(() => setReportSummaryData("Error fetching summary."))
        .finally(() => setReportSummaryLoading(false));
    };
    window.addEventListener('naira-load-summary', handler);
    return () => window.removeEventListener('naira-load-summary', handler);
  }, []);
  const [useRedTheme, setUseRedTheme] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [chatHistoryError, setChatHistoryError] = useState(null);

  const userContext = "Suggest answer in 300 words only";

  const topQuestions = [
    ...(isHtmlReport && publicationId ? [{ label: "Summarize this report", value: "Summarize this report", isSummary: true }] : []),
    { label: "What is Nomura's Fed rate call?", value: "What is Nomura's Fed rate call?" },
    { label: "What are your top trade ideas?", value: "What are your top trade ideas?" },
    { label: "What is the China economic outlook?", value: "What is the China economic outlook?" }
  ];

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const now = new Date();
    const isToday =
      ts.getDate() === now.getDate() &&
      ts.getMonth() === now.getMonth() &&
      ts.getFullYear() === now.getFullYear();
    const time = ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (isToday) return time;
    return ts.toLocaleDateString([], { month: "short", day: "numeric" }) + ` ${time}`;
  };

  const handleNewChat = () => {
    const convId = generateConversationId();
    setMessages([]);
    setInput("");
    setBotResponseCount(0);
    setConversationId(convId);
    setSelectedOptions(
      eqEnabled
        ? { all: true, economics: true, fx: true, fi: true, eq: true }
        : { all: true, economics: true, fx: true, fi: true }
    );
    setShowContinuePrompt(false);
    setLoading(false);
    setFeedback({});
    return convId;
  };

  const handleReportSummaryChange = (e) => {
    const { value } = e.target;
    const entries = value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v);
    const urlPattern = /^(https?:\/\/[^\s]+)$/;
    const numberPattern = /^\d+$/;
    const invalid = entries.some((entry) => !(urlPattern.test(entry) || numberPattern.test(entry)));
    setReportSummaryInput(value);
    setReportSummaryError(invalid ? "Only numbers or valid URLs allowed, separated by commas." : "");
  };

  const handleSummarizeReport = async () => {
    const userMessage = { from: "user", text: "Summarize this report", timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    try {
      const result = await getSummaryService(String(publicationId));
      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: result.responseBody?.answer || "No summary available.",
          isHtml: true,
          timestamp: new Date(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "Error fetching summary.", timestamp: new Date() },
      ]);
    } finally {
      setLoading(false);
      setShowContinuePrompt(true);
    }
  };

  const handleGetSummary = async () => {
    if (!reportSummaryError && reportSummaryInput.trim()) {
      setReportSummaryLoading(true);
      try {
        const result = await getSummaryService(reportSummaryInput);
        setReportSummaryData(result.responseBody?.answer || "");
      } catch {
        setReportSummaryData("Error fetching summary.");
      } finally {
        setReportSummaryLoading(false);
      }
    }
  };

  const handleSend = async (question, displayLabel) => {
    const userQuestion = question ?? input;
    const displayText = displayLabel ?? userQuestion;
    const convId = question ? handleNewChat() : conversationId;
    if (!userQuestion.trim()) return;
    const userMessage = { from: "user", text: displayText, timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      let assetClass;
      if (selectedOptions.all) {
        assetClass = ["Economics", "FX", "FI"];
        if (eqEnabled) assetClass.push("EQ");
      } else {
        assetClass = [];
        if (selectedOptions.economics) assetClass.push("Economics");
        if (selectedOptions.fx) assetClass.push("FX");
        if (selectedOptions.fi) assetClass.push("FI");
        if (eqEnabled && selectedOptions.eq) assetClass.push("EQ");
        if (!assetClass.length) assetClass = ["All"];
      }
      const messageIndex = botResponseCount + 1;
      const startTime = new Date();
      const data = await getResponseService(userQuestion, assetClass, convId, messageIndex, deepSearch);
      const endTime = new Date();
      const responseTime = endTime - startTime;

      setBotResponseCount(messageIndex);
      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: data.responseBody.answer,
          question_id: data.responseBody.question_id,
          conv_id: convId,
          isHtml: true,
          timestamp: endTime,
          responseTime
        }
      ]);
      const nowISO = endTime.toISOString();
      setChatHistory((prev) => {
        const exists = prev.some((c) => c.conv_id === convId);
        if (exists) {
          return prev.map((c) =>
            c.conv_id === convId
              ? { ...c, last_activity: nowISO, message_count: (c.message_count || 0) + 1 }
              : c
          );
        }

        return [
          {
            conv_id: convId,
            last_activity: nowISO,
            started_at: nowISO,
            message_count: 1,
            messages: [{ question: displayText, question_time: nowISO, answer: data.responseBody.answer, answer_time: nowISO }],
            asset_class: assetClass,
          },
          ...prev,
        ];
      });
      fetchChatHistory();
    } catch (e) {
      console.error("Naira connection error :", e.message);
      setMessages((prev) => [...prev, { from: "bot", text: "Error fetching response.", timestamp: new Date() }]);
    } finally {
      setLoading(false);
      setShowContinuePrompt(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const handleContinueNo = () => setShowContinuePrompt(false);
  const handleContinueYes = () => {
    setShowContinuePrompt(false);
    handleNewChat();
  };

  const fetchChatHistory = useCallback(async () => {
    setChatHistoryLoading(true);
    setChatHistoryError(null);
    try {
      const response = await getConversationHistoryService();
      setChatHistory(response?.responseBody?.conversations ?? []);
    } catch (err) {
      console.error("Error fetching conversation history:", err);
      setChatHistoryError("Failed to load conversation history. Please try again.");
    } finally {
      setChatHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChatHistory();
  }, [fetchChatHistory]);

  const handleOpenConversation = (conversation) => {
    setMessages([]);
    setInput("");
    setShowContinuePrompt(false);
    setLoading(false);
    setFeedback({});

    const loadedMessages = (conversation.messages ?? [])
      .slice()
      .sort((a, b) => {
        const timeA = new Date(a.question_time).getTime() || 0;
        const timeB = new Date(b.question_time).getTime() || 0;
        return timeA - timeB;
      })
      .flatMap((msg) => [
        {
          from: "user",
          text: msg.question,
          timestamp: msg.question_time ? new Date(msg.question_time) : new Date(),
          conv_id: conversation.conv_id,
          question_id: msg.question_id,
          historical: true,
        },
        {
          from: "bot",
          text: msg.answer,
          timestamp: msg.answer_time ? new Date(msg.answer_time) : new Date(),
          conv_id: conversation.conv_id,
          question_id: msg.question_id,
          message_index: msg.message_index,
          isHtml: true,
          historical: true,
        },
      ]);

    setMessages(loadedMessages);
    setConversationId(conversation.conv_id);
    setBotResponseCount(conversation.message_count || 0);

    const ac = conversation.asset_class ?? [];
    const isAllString = ac.includes("All");
    const allClasses = eqEnabled
      ? ["Economics", "FX", "FI", "EQ"]
      : ["Economics", "FX", "FI"];
    const hasAll = isAllString || allClasses.every((c) => ac.includes(c));
    setSelectedOptions({
      all: hasAll,
      economics: isAllString || ac.includes("Economics"),
      fx: isAllString || ac.includes("FX"),
      fi: isAllString || ac.includes("FI"),
      ...(eqEnabled && { eq: isAllString || ac.includes("EQ") }),
    });

    setTab(0);
  };

  const customTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "light",
          primary: {
            main: useRedTheme ? "rgba(202, 36, 32, 0.85)" : "#00A9E3",
            contrastText: "#FFFFFFD9",
            chatBackground: useRedTheme ? "#FFFFFFD9" : "#DFF2FB"
          }
        },
        components: {
          MuiCheckbox: {
            styleOverrides: {
              root: {
                "&.Mui-checked": { color: useRedTheme ? "rgba(202, 36, 32, 0.85)" : "#00A9E3" },
                "&.Mui-checked:hover": {
                  backgroundColor: useRedTheme ? "rgba(220,0,78,0.08)" : "rgba(0,169,227,0.08)"
                }
              }
            }
          }
        }
      }),
    [useRedTheme]
  );

  const onToggleTheme = () => setUseRedTheme((p) => !p);

  return {
    isHtmlReport,
    publicationId,
    // state
    messages,
    input,
    loading,
    showContinuePrompt,
    feedback,
    showIntroSection,
    tab,
    fullscreen,
    deepSearch,
    selectedOptions,
    reportSummaryInput,
    reportSummaryError,
    reportSummaryData,
    reportSummaryLoading,
    userContext,
    topQuestions,
    // setters / handlers
    setShowIntroSection,
    setInput,
    setTab,
    setFullscreen,
    setDeepSearch,
    setSelectedOptions,
    setFeedback,
    handleSend,
    handleKeyDown,
    handleContinueNo,
    handleContinueYes,
    handleNewChat,
    handleReportSummaryChange,
    handleGetSummary,
    handleOpenConversation,
    formatTimestamp,
    customTheme,
    onToggleTheme,
    chatHistory,
    chatHistoryLoading,
    chatHistoryError,
    fetchChatHistory,
    handleSummarizeReport,
  };
}